import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import i18n from '@/i18n';
import { pageMaxClass } from '@/utils/pageWidth';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useTheme } from '@/hooks/useTheme';
import { useMarketPricesUi, shiftDayIso, type MarketPeriod } from '@/hooks/useMarketPricesUi';
import SegmentedTabs from '@/components/base/SegmentedTabs';
import { Download } from 'lucide-react';
import ExcelExportPanel from './ExcelExportPanel';
import RdnPricesDay from './RdnPricesDay';
import RdnPricesMonth from './RdnPricesMonth';
import { API_BASE_URL, authFetch } from '@/services/api';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

type Tab = 'rdn' | 'idm' | 'br_oper' | 'br_fact';
type View = 'chart' | 'table';

interface HourRow { hour: number; price: number | null; volume: number | null; hh: string }
interface DamResp {
  tradeDate: string | null;
  hours: { hour: number; price: number | null; volume: number | null }[];
}

interface BrHour { hour: number; pos: number | null; neg: number | null; imb: number | null; hh: string }
interface BrSummary {
  minPos: number | null; maxPos: number | null;
  minNeg: number | null; maxNeg: number | null;
}
interface BrResp {
  tradeDate: string | null;
  isLatestDefault: boolean;
  dataRange: { min: string | null; max: string | null };
  hours: { hour: number; pos: number | null; neg: number | null; imb: number | null }[];
  summary: BrSummary | null;
}

interface Zone { wavg: number | null; prevWavg: number | null; changePct: number | null }
interface DamSummary {
  tradeDate: string | null;
  dataRange: { min: string | null; max: string | null };
  tiles: {
    day: Zone; day_8_23: Zone; night: Zone; peak: Zone; semipeak: Zone;
    month: { ym: string; wavg: number | null; prevYm: string; prevWavg: number | null; changePct: number | null };
  } | null;
}

const PRICE = '#E7476B';   // pink price line (matches OREE)
const VOL = '#2E9BD6';     // blue volume bars
const POS_C = '#22C55E';   // positive imbalance — green
const NEG_C = '#EF4444';   // negative imbalance — red
const UP_C = '#22C55E';    // price growth — green (per OREE text-success)
const DOWN_C = '#EF4444';  // price fall — red (per OREE text-danger)
const PRICE_TICKS = [0, 2000, 4000, 6000, 8000, 10000, 12000, 14000, 16000];
const OREE_URL = 'https://www.oree.com.ua';
const UA_ENERGY_URL = 'https://ua.energy/uchasnikam_rinku/rezultaty-balansuyuchogo-rynku-2/';

const loc = () => (i18n.language === 'en' ? 'en-GB' : 'uk-UA');
const num = (n: number | null | undefined, d = 1) =>
  n == null ? '—' : Number(n).toLocaleString(loc(), { minimumFractionDigits: d, maximumFractionDigits: d });
const num2 = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString(loc(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dmy = (iso: string) => (iso ? iso.split('-').reverse().join('.') : '');
const shiftDay = (iso: string, delta: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};

/** Theme tokens shared by every block on this page. */
function useTokens() {
  const { isDark } = useTheme();
  return {
    isDark,
    cardBg: isDark ? '#1A1A1A' : '#FFFFFF',
    cardBorder: isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8',
    textMain: isDark ? '#E0E0E0' : '#1A1A1A',
    textMuted: '#6B6B70',
    textDim: isDark ? '#4A4A50' : '#9B9BA4',
    innerBg: isDark ? '#0F0F0F' : '#F5F5F7',
    rowHi: isDark ? 'rgba(255,255,255,0.05)' : '#F0F1F3',
  };
}

/** ◀ дата ▶ picker. `bold` renders the date prominently (РДН header). */
function DateNav({ value, onChange, min, max, bold = false }:
  { value: string; onChange: (v: string) => void; min?: string | null; max?: string | null; bold?: boolean }) {
  const T = useTokens();
  const canPrev = !!value && (!min || value > min);
  const canNext = !!value && (!max || value < max);
  const btn = (dir: -1 | 1, enabled: boolean) => (
    <button onClick={() => enabled && onChange(shiftDay(value, dir))} disabled={!enabled}
      className="flex items-center justify-center rounded-full transition-colors"
      style={{
        width: bold ? 36 : 32, height: bold ? 36 : 32, background: T.innerBg,
        border: `1px solid ${T.cardBorder}`, color: enabled ? T.textMain : T.textDim,
        cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.5,
      }}>
      <i className={dir === -1 ? 'ri-arrow-left-s-line' : 'ri-arrow-right-s-line'} style={{ fontSize: bold ? 20 : 18 }} />
    </button>
  );
  return (
    <div className="flex items-center gap-2">
      {btn(-1, canPrev)}
      <input type="date" value={value} min={min || undefined} max={max || undefined}
        onChange={(e) => e.target.value && onChange(e.target.value)}
        className="rounded-full outline-none cursor-pointer"
        style={{
          background: T.innerBg, border: `1px solid ${T.cardBorder}`, color: T.textMain,
          colorScheme: T.isDark ? 'dark' : 'light',
          padding: bold ? '9px 14px' : '8px 12px',
          fontSize: bold ? 15 : 14, fontWeight: bold ? 700 : 400,
        }} />
      {btn(1, canNext)}
    </div>
  );
}

/** Horizontal (transposed) hourly table: first row = hours, then one row per series. */
function TransposedTable({ hours, rows }:
  { hours: string[]; rows: { label: string; cells: string[]; color?: string }[] }) {
  const { t } = useTranslation();
  const T = useTokens();
  const cell: any = {
    padding: '8px 10px', fontSize: 13, textAlign: 'center', color: T.textMain,
    borderBottom: `1px solid ${T.cardBorder}`, borderRight: `1px solid ${T.cardBorder}`, whiteSpace: 'nowrap',
  };
  const head: any = { ...cell, fontWeight: 700, color: T.textMuted, background: T.rowHi };
  const label: any = {
    ...cell, textAlign: 'left', fontWeight: 600, color: T.textMuted, background: T.cardBg,
    position: 'sticky', left: 0, minWidth: 190,
  };
  return (
    <div className="overflow-x-auto" style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 8 }}>
      <table style={{ borderCollapse: 'collapse', minWidth: 900 }}>
        <tbody>
          <tr>
            <td style={label}>{t('ak_prices_hour')}</td>
            {hours.map((h) => <td key={h} style={head}>{h}</td>)}
          </tr>
          {rows.map((r) => (
            <tr key={r.label}>
              <td style={label}>{r.label}</td>
              {r.cells.map((c, i) => (
                <td key={i} style={{ ...cell, color: r.color || T.textMain, fontWeight: r.color ? 600 : 400 }}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Pill toggle used for Графік / Таблиця. */
function ViewToggle({ view, setView }: { view: View; setView: (v: View) => void }) {
  const { t } = useTranslation();
  const T = useTokens();
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {(([['chart', t('ak_prices_chart')], ['table', t('ak_prices_table')]]) as [View, string][]).map(([v, lbl]) => (
        <button key={v} onClick={() => setView(v)} className="text-sm rounded-full transition-colors"
          style={{
            padding: '8px 20px',
            background: view === v ? (T.isDark ? 'rgba(255,255,255,0.06)' : '#F3F4F6') : 'transparent',
            color: view === v ? T.textMain : T.textMuted,
            border: `1px solid ${view === v ? T.cardBorder : 'transparent'}`,
            fontWeight: view === v ? 600 : 400,
          }}>
          {lbl}
        </button>
      ))}
    </div>
  );
}

// ---- Balancing-market hourly prices (shared by «БР — фактичні» / «БР — оперативні») ----
function BrPricesView({ endpoint, title }: { endpoint: string; title: string }) {
  const { t } = useTranslation();
  const T = useTokens();
  const [date, setDate] = useState('');            // '' → backend resolves to latest available
  const [data, setData] = useState<BrResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('chart');

  useEffect(() => {
    setLoading(true);
    authFetch(`${API_BASE_URL}${endpoint}${date ? `?date=${date}` : ''}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d: BrResp) => { setData(d); if (!date && d.tradeDate) setDate(d.tradeDate); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [endpoint, date]);

  const hours: BrHour[] = useMemo(
    () => (data?.hours || []).map((h) => ({ ...h, hh: String(h.hour).padStart(2, '0') })), [data]);
  const min = data?.dataRange?.min || null;
  const max = data?.dataRange?.max || null;
  const sm = data?.summary;

  const chip = (label: string, value: number | null | undefined, color: string) => (
    <span key={label} className="inline-flex items-center gap-1 rounded-full"
      style={{ background: T.innerBg, border: `1px solid ${T.cardBorder}`, padding: '4px 10px', fontSize: 11, whiteSpace: 'nowrap' }}>
      <span style={{ color: T.textMuted }}>{label}</span>
      <b style={{ color }}>{num2(value)}</b>
    </span>
  );

  const BrTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const get = (k: string) => payload.find((p: any) => p.dataKey === k)?.value;
    return (
      <div style={{ background: T.innerBg, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: T.textMain }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{dmy(data?.tradeDate || date)} · {t('ak_prices_hour_00', { h: label })}</div>
        <div style={{ color: POS_C }}>{t('ak_prices_positive_imbalance_uah_mwh', { v: num2(get('pos')) })}</div>
        <div style={{ color: NEG_C }}>{t('ak_prices_negative_imbalance_uah_mwh', { v: num2(get('neg')) })}</div>
      </div>
    );
  };

  return (
    <div className="rounded-xl p-4 sm:p-5" style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}` }}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="shrink-0">
          <div className="text-lg font-bold" style={{ color: T.textMain }}>{title}</div>
          <a href={UA_ENERGY_URL} target="_blank" rel="noopener noreferrer"
            className="text-xs mt-0.5 inline-block hover:underline" style={{ color: VOL }}>
            {t('ak_prices_source_npc_ukrenergo')}
          </a>
        </div>
        {sm && (
          <div className="flex items-center gap-2 flex-wrap justify-center flex-1">
            {chip(t('ak_prices_min_positive'), sm.minPos, POS_C)}
            {chip(t('ak_prices_max_positive'), sm.maxPos, POS_C)}
            {chip(t('ak_prices_min_negative'), sm.minNeg, NEG_C)}
            {chip(t('ak_prices_max_negative'), sm.maxNeg, NEG_C)}
          </div>
        )}
        <div className="shrink-0"><DateNav value={date} onChange={setDate} min={min} max={max} /></div>
      </div>

      <div className="mb-3"><ViewToggle view={view} setView={setView} /></div>

      {loading ? (
        <div className="flex items-center justify-center text-xs" style={{ color: T.textMuted, height: 420 }}>{t('ak_prices_loading')}</div>
      ) : hours.length === 0 ? (
        <div className="flex items-center justify-center text-xs text-center" style={{ color: T.textMuted, height: 420 }}>
          {t('ak_prices_no_imbalance_prices_for', { d: dmy(date) })}
        </div>
      ) : view === 'chart' ? (
        <div style={{ height: 440 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={hours} margin={{ top: 10, right: 12, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.cardBorder} vertical={false} />
              <XAxis dataKey="hh" tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false} interval={0} />
              <YAxis width={72} tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false}
                tickFormatter={(v: number) => v.toLocaleString(loc())}
                label={{ value: t('ak_prices_balancing_market_price_uah_mwh'), angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: T.textMuted, textAnchor: 'middle' } }} />
              <Tooltip content={<BrTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="pos" name={t('ak_prices_positive_imbalance')} stroke={POS_C} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls />
              <Line type="monotone" dataKey="neg" name={t('ak_prices_negative_imbalance')} stroke={NEG_C} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <TransposedTable
          hours={hours.map((h) => h.hh)}
          rows={[
            { label: t('ak_prices_positive_imbalance_uah_mwh_2'), cells: hours.map((h) => num2(h.pos)), color: POS_C },
            { label: t('ak_prices_negative_imbalance_uah_mwh_2'), cells: hours.map((h) => num2(h.neg)), color: NEG_C },
          ]} />
      )}
    </div>
  );
}

// ---- 6 РДН price tiles (presentational — date is owned by the page) ----
function RdnSummaryTiles({ summary, loading }: { summary: DamSummary | null; loading: boolean }) {
  useTranslation();
  const T = useTokens();
  const tl = summary?.tiles || null;
  const val = (x: number | null | undefined) => (x == null ? 'N/A' : num(x, 2));

  const ChangeRow = ({ pct }: { pct: number | null | undefined }) =>
    pct != null ? (
      <div className="text-xs font-semibold mt-0.5" style={{ color: pct > 0 ? UP_C : pct < 0 ? DOWN_C : T.textMuted }}>
        {pct > 0 ? '↑' : pct < 0 ? '↓' : '→'} {num(Math.abs(pct), 2)}%
      </div>
    ) : <div className="text-xs mt-0.5" style={{ color: T.textDim }}>—</div>;

  const Tile = ({ title, z, sub }: { title: string; z?: Zone; sub: string }) => (
    <div className="rounded-xl p-3 flex flex-col" style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, minHeight: 96 }}>
      <div className="text-[11px] font-medium" style={{ color: T.textMuted, whiteSpace: 'nowrap' }}>{title}</div>
      <div className="text-xl font-bold mt-1 leading-tight" style={{ color: z?.wavg == null ? T.textDim : T.textMain }}>{val(z?.wavg)}</div>
      <ChangeRow pct={z?.changePct} />
      <div className="text-[10px] mt-auto pt-1" style={{ color: T.textDim }}>{sub}</div>
    </div>
  );

  const D1 = i18n.t('ak_prices_uah_mwh_vs_d_1');
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
      style={{ opacity: loading ? 0.5 : 1, transition: 'opacity .15s' }}>
      <Tile title={i18n.t('ak_prices_daily')} z={tl?.day} sub={D1} />
      <Tile title={i18n.t('ak_prices_day_8_23')} z={tl?.day_8_23} sub={D1} />
      <Tile title={i18n.t('ak_prices_night_1_7_24')} z={tl?.night} sub={D1} />
      <Tile title={i18n.t('ak_prices_peak_18_23')} z={tl?.peak} sub={D1} />
      <Tile title={i18n.t('ak_prices_semi_peak_8_17')} z={tl?.semipeak} sub={D1} />
      <Tile title={i18n.t('ak_prices_monthly')}
        z={tl ? { wavg: tl.month.wavg, prevWavg: tl.month.prevWavg, changePct: tl.month.changePct } : undefined}
        sub={i18n.t('ak_prices_uah_mwh_vs', { ym: tl?.month.ym ?? '', prev: tl?.month.prevYm ?? '' })} />
    </div>
  );
}

function LegacyMarketPrices() {
  const { t } = useTranslation();
  const T = useTokens();
  const [tab, setTab] = useState<Tab>('rdn');
  const [view, setView] = useState<View>('chart');
  // Single source of truth for the РДН tab: one date drives both tiles and the journal.
  const [date, setDate] = useState('');            // '' → backend resolves to latest priced day
  const [summary, setSummary] = useState<DamSummary | null>(null);
  const [dam, setDam] = useState<DamResp | null>(null);
  const [sumLoading, setSumLoading] = useState(true);
  const [damLoading, setDamLoading] = useState(true);

  useEffect(() => {
    if (tab !== 'rdn') return;
    setSumLoading(true);
    authFetch(`${API_BASE_URL}/market/dam-summary${date ? `?date=${date}` : ''}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d: DamSummary) => { setSummary(d); if (!date && d.tradeDate) setDate(d.tradeDate); })
      .catch(() => setSummary(null))
      .finally(() => setSumLoading(false));
  }, [tab, date]);

  useEffect(() => {
    if (tab !== 'rdn' || !date) return;
    setDamLoading(true);
    authFetch(`${API_BASE_URL}/market/dam-hourly?date=${date}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setDam(d))
      .catch(() => setDam(null))
      .finally(() => setDamLoading(false));
  }, [tab, date]);

  const hours: HourRow[] = useMemo(
    () => (dam?.hours || []).map((h) => ({ ...h, hh: String(h.hour).padStart(2, '0') })), [dam]);
  const volMax = useMemo(() => {
    const mx = Math.max(0, ...hours.map((h) => h.volume ?? 0));
    return mx > 0 ? Math.ceil(mx / 1000) * 1000 : 5000;
  }, [hours]);

  const TABS: { k: Tab; label: string }[] = [
    { k: 'rdn', label: t('ak_prices_dam_day_ahead') },
    { k: 'br_oper', label: t('ak_prices_bm_operational') },
    { k: 'br_fact', label: t('ak_prices_bm_actual') },
  ];

  return (
    <DashboardLayout breadcrumb={[t('ak_prices_market_prices')]}>
      <div style={{ color: T.textMain, maxWidth: '100%' }}>
        {/* market tabs */}
        <div className="flex items-center gap-1 flex-wrap mb-4">
          {TABS.map((tb) => {
            const active = tab === tb.k;
            return (
              <button key={tb.k} onClick={() => setTab(tb.k)} className="text-sm rounded-lg transition-colors"
                style={{
                  padding: '6px 12px',
                  background: active ? (T.isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB') : (T.isDark ? 'rgba(255,255,255,0.04)' : '#F3F4F6'),
                  color: active ? T.textMain : T.textMuted, fontWeight: active ? 600 : 400,
                  border: `1px solid ${active ? (T.isDark ? 'rgba(255,255,255,0.18)' : '#D1D5DB') : 'transparent'}`,
                }}>
                {tb.label}
              </button>
            );
          })}
        </div>

        {tab === 'rdn' ? (
          <>
            {/* shared date navigator + section heading */}
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="text-lg font-bold" style={{ color: T.textMain }}>{t('ak_prices_weighted_average_dam_prices')}</div>
                <div className="text-xs mt-0.5" style={{ color: T.textMuted }}>
                  {t('ak_prices_data_source_market_operator_jsc')}{' '}
                  <a href={OREE_URL} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: VOL }}>
                    www.oree.com.ua
                  </a>
                </div>
              </div>
              <DateNav value={date} onChange={setDate}
                min={summary?.dataRange?.min} max={summary?.dataRange?.max} bold />
            </div>

            <div className="mb-4"><RdnSummaryTiles summary={summary} loading={sumLoading} /></div>

            <div className="rounded-xl p-4 sm:p-5" style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}` }}>
              <div className="text-lg font-bold mb-3" style={{ color: T.textMain }}>{t('ak_prices_hourly_dam_prices')}</div>
              <div className="mb-3"><ViewToggle view={view} setView={setView} /></div>

              {damLoading ? (
                <div className="flex items-center justify-center text-xs" style={{ color: T.textMuted, height: 360 }}>{t('ak_prices_loading')}</div>
              ) : hours.length === 0 ? (
                <div className="flex items-center justify-center text-xs text-center" style={{ color: T.textMuted, height: 360 }}>
                  {t('ak_prices_no_dam_data_for', { d: dmy(date) })}<br />{t('ak_prices_prices_for_tomorrow_appear_today_at_12')}
                </div>
              ) : view === 'chart' ? (
                <div style={{ height: 420 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={hours} margin={{ top: 10, right: 4, bottom: 4, left: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.cardBorder} vertical={false} />
                      <XAxis dataKey="hh" tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false} interval={0} />
                      <YAxis yAxisId="price" domain={[0, 16000]} ticks={PRICE_TICKS} width={64}
                        tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false}
                        tickFormatter={(v: number) => v.toLocaleString(loc())}
                        label={{ value: t('ak_prices_price_uah_mwh'), angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: T.textMuted, textAnchor: 'middle' } }} />
                      <YAxis yAxisId="vol" orientation="right" domain={[0, volMax]} width={56}
                        tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false}
                        label={{ value: t('ak_prices_volume_mwh'), angle: 90, position: 'insideRight', style: { fontSize: 11, fill: T.textMuted, textAnchor: 'middle' } }} />
                      <Tooltip contentStyle={{ background: T.innerBg, border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 12, color: T.textMain }}
                        formatter={(v: any, n: any) => (n === t('ak_prices_price') ? [t('ak_prices_uah_mwh', { n: num(v, 0) }), n] : [t('ak_prices_mwh', { n: num(v, 1) }), n])}
                        labelFormatter={(l: string) => t('ak_prices_hour_00', { h: l })} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar yAxisId="vol" dataKey="volume" name={t('ak_prices_volume')} fill={VOL} fillOpacity={0.18} stroke={VOL} strokeWidth={1} strokeOpacity={1} maxBarSize={26} />
                      <Line yAxisId="price" type="monotone" dataKey="price" name={t('ak_prices_price')} stroke={PRICE} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <TransposedTable
                  hours={hours.map((h) => h.hh)}
                  rows={[
                    { label: t('ak_prices_price_uah_mwh'), cells: hours.map((h) => num(h.price, 0)), color: PRICE },
                    { label: t('ak_prices_volume_mwh'), cells: hours.map((h) => num(h.volume, 1)), color: VOL },
                  ]} />
              )}
            </div>
          </>
        ) : tab === 'br_fact' ? (
          <BrPricesView key="br_fact" endpoint="/market/br-fact-hourly"
            title={t('ak_prices_actual_hourly_balancing_market_prices')} />
        ) : (
          <BrPricesView key="br_oper" endpoint="/market/br-oper-hourly"
            title={t('ak_prices_operational_hourly_balancing_market_pr')} />
        )}
      </div>
    </DashboardLayout>
  );
}

// ═══════════════════════ РЕДИЗАЙН (VITE_REDESIGN) ═══════════════════════
// Токен-версія сторінки: операційні контроли (вкладка ринку · дата · Графік/
// Таблиця) винесені в топбар (useMarketPricesUi), тіло — лише презентація на
// скляних .tile у дизайн-токенах, догмі й Urbanist. Прод (без VITE_REDESIGN)
// бачить незмінний LegacyMarketPrices вище.

/** Кольори серій/осей із дизайн-токенів (перечитуються при зміні теми). */
function useChartColors() {
  const { isDark } = useTheme();
  return useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const v = (n: string, fb: string) => css.getPropertyValue(n).trim() || fb;
    return {
      // Ціна РДН — приглушений «нейтралізований» тон із наданих рамп colour-mixing
      // (--series-3 = 50% мікс #28323C↔червоний), НЕ яскравий #D23538: той читався
      // як помилка/збій. Той самий тон — для посилань-джерел нижче.
      price: v('--series-3', '#A6575B'),
      vol: v('--series-2', '#5E656D'),
      ok: v('--ok', '#2E7D46'),
      warn: v('--warn', '#C77A1B'),
      grid: v('--line', '#E4E5E7'),
      text: v('--text', '#28323C'),
      muted: v('--text-muted', '#5E656D'),
      dim: v('--text-dim', '#9AA0A6'),
      surface: v('--surface-solid', '#FFFFFF'),
    };
  }, [isDark]);
}

/** Скляна плитка зони РДН. Зміна: ↑ok / ↓warn / →dim (без червоного — догма). */
function RdZoneTile({ title, z, sub }: { title: string; z?: Zone; sub: string }) {
  const pct = z?.changePct;
  const up = pct != null && pct > 0, down = pct != null && pct < 0;
  const chColor = up ? 'var(--ok)' : down ? 'var(--warn)' : 'var(--text-dim)';
  return (
    <div className="tile p-3.5 flex flex-col" style={{ minHeight: 104 }}>
      <div className="text-[11px] font-medium truncate" style={{ color: 'var(--text-muted)' }}>{title}</div>
      <div className="font-display text-[22px] leading-tight mt-1" style={{ color: z?.wavg == null ? 'var(--text-dim)' : 'var(--text)' }}>
        {z?.wavg == null ? 'N/A' : num(z.wavg, 2)}
      </div>
      {pct != null ? (
        <div className="text-xs font-semibold mt-0.5" style={{ color: chColor }}>{up ? '↑' : down ? '↓' : '→'} {num(Math.abs(pct), 2)}%</div>
      ) : <div className="text-xs mt-0.5" style={{ color: 'var(--text-dim)' }}>—</div>}
      <div className="text-[10px] mt-auto pt-1" style={{ color: 'var(--text-dim)' }}>{sub}</div>
    </div>
  );
}

/** Токен-версія транспонованої таблиці (година у шапці, серії — рядками). */
function RdTable({ hours, rows }: { hours: string[]; rows: { label: string; cells: string[]; color?: string }[] }) {
  const { t } = useTranslation();
  const cell: any = { padding: '8px 10px', fontSize: 13, textAlign: 'center', color: 'var(--text)', borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line)', whiteSpace: 'nowrap' };
  const head: any = { ...cell, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--surface-2)' };
  const label: any = { ...cell, textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', background: 'var(--surface-solid)', position: 'sticky', left: 0, minWidth: 210 };
  return (
    <div className="overflow-x-auto" style={{ border: '1px solid var(--line)', borderRadius: 12 }}>
      <table style={{ borderCollapse: 'collapse', minWidth: 900 }}>
        <tbody>
          <tr><td style={label}>{t('ak_prices_hour')}</td>{hours.map((hh) => <td key={hh} style={head}>{hh}</td>)}</tr>
          {rows.map((r) => (
            <tr key={r.label}><td style={label}>{r.label}</td>{r.cells.map((cc, i) => <td key={i} style={{ ...cell, color: r.color || 'var(--text)', fontWeight: r.color ? 600 : 400 }}>{cc}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RdEmpty({ text, height }: { text: ReactNode; height: number }) {
  return <div className="flex items-center justify-center text-xs text-center px-4" style={{ color: 'var(--text-muted)', height }}>{text}</div>;
}

// ═══ «Ціни» v2 (29.10.2026) — одна сторінка: РДН · ВДР · БР; прогноз РДН — на тій самій шкалі, що й факт ═══
// Каркас, контроли й плитки — за макетом /tg (нарада 28.10); логіка прогнозу — наша (RdnPricesDay / RdnPricesMonth).

/** Кругла кнопка «завантажити»: РДН/ВДР — вікно «Завантажити в Excel»; БР — одразу CSV погодинного ряду. */
function RdDownloadBtn({ kind, title, onCsv }: { kind: 'cards' | 'hourly'; title: string; onCsv?: () => void }) {
  const { tab, date, min, max, period } = useMarketPricesUi();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [open]);
  return (
    <div ref={ref} className="relative shrink-0">
      <button type="button" onClick={() => (onCsv ? onCsv() : setOpen((o) => !o))} title={title} aria-label={title}
        className="flex items-center justify-center cursor-pointer transition-colors"
        style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--line-strong)', color: 'var(--text-muted)' }}>
        <Download size={17} strokeWidth={2} />
      </button>
      {open && !onCsv && (
        <ExcelExportPanel kind={kind} market={tab === 'idm' ? 'IDM' : 'DAM'} pageDate={date} min={min} max={max}
          initialPeriod={tab === 'rdn' && period === 'month' ? 'month' : 'day'} onClose={() => setOpen(false)} />
      )}
    </div>
  );
}

/** Графік / Таблиця + завантаження погодинних даних — між рядом плиток і плиткою графіка (праворуч), як у /tg. */
function RdViewSwitch({ onCsv }: { onCsv?: () => void }) {
  const { t } = useTranslation();
  const { view, setView } = useMarketPricesUi();
  return (
    <div className="flex items-center gap-2">
      <SegmentedTabs items={[{ key: 'chart', label: t('ak_prices_chart') }, { key: 'table', label: t('ak_prices_table') }]}
        activeKey={view} onSelect={(k) => setView(k as View)} />
      <RdDownloadBtn kind="hourly" title={t('pv2_download_hourly')} onCsv={onCsv} />
    </div>
  );
}

/** CSV (UTF-8 з BOM, «;» і десяткова кома — Excel відкриває колонки одразу). */
function downloadCsv(name: string, head: string[], rows: (string | number | null)[][]) {
  const cell = (v: string | number | null) => {
    if (v == null) return '';
    const s = typeof v === 'number' ? String(v).replace('.', ',') : v;
    return /[;"\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = '﻿' + [head, ...rows].map((r) => r.map(cell).join(';')).join('\r\n');
  const u = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = u; a.download = `${name}.csv`; a.click();
  URL.revokeObjectURL(u);
}

// ── ВДР: ті ж зони, що в /tg (BASE — середня; решта — зважені за обсягом), динаміка проти D−1 ──
type DayHours = { hour: number; price: number | null; volume: number | null }[];
function vwap(rows: DayHours, pick: (h: number) => boolean): number | null {
  let pv = 0, v = 0;
  for (const r of rows) if (pick(r.hour) && r.price != null && r.volume != null && r.volume > 0) { pv += r.price * r.volume; v += r.volume; }
  return v > 0 ? pv / v : null;
}
function baseOf(hours?: { price: number | null }[] | null): number | null {
  const p = (hours ?? []).map((h) => h.price).filter((x): x is number => x != null);
  return p.length ? p.reduce((a, b) => a + b, 0) / p.length : null;
}

function IdmRedesign({ date, view }: { date: string; view: View }) {
  const { t } = useTranslation();
  const c = useChartColors();
  const { setRange, setDate } = useMarketPricesUi();
  const [hours, setHours] = useState<DayHours | null>(null);
  const [prev, setPrev] = useState<DayHours | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    authFetch(`${API_BASE_URL}/trading/oree/daily?days=400&market=IDM`).then((r) => (r.ok ? r.json() : null)).then((d: { rows?: { trade_date: string }[] } | null) => {
      const ds = (d?.rows ?? []).map((x) => String(x.trade_date).slice(0, 10)).sort();
      if (dead || !ds.length) return;
      setRange(ds[0], ds[ds.length - 1]);
      if (!date) setDate(ds[ds.length - 1]);
    }).catch(() => {});
    return () => { dead = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!date) return;
    let dead = false;
    setLoading(true);
    const load = (d: string): Promise<DayHours> =>
      authFetch(`${API_BASE_URL}/trading/oree/hourly?date=${d}`).then((r) => (r.ok ? r.json() : null))
        .then((j: { rows?: { hour: number; market: string; price: number | null; volume: number | null }[] } | null) =>
          (j?.rows ?? []).filter((x) => x.market === 'IDM').map((x) => ({ hour: x.hour, price: x.price, volume: x.volume })).sort((a, b) => a.hour - b.hour));
    Promise.all([load(date), load(shiftDayIso(date, -1))])
      .then(([a, b]) => { if (!dead) { setHours(a); setPrev(b); } })
      .catch(() => { if (!dead) { setHours(null); setPrev(null); } })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [date]);

  const zones = useMemo(() => {
    const defs: [string, (h: number) => boolean][] = [
      [t('pv2_weighted'), () => true], [t('ak_prices_day_8_23'), (h) => h >= 8 && h <= 23], [t('ak_prices_night_1_7_24'), (h) => h <= 7 || h === 24],
      [t('ak_prices_peak_18_23'), (h) => h >= 18 && h <= 23], [t('ak_prices_semi_peak_8_17'), (h) => h >= 8 && h <= 17],
    ];
    const zoneOf = (a: number | null, b: number | null): Zone =>
      ({ wavg: a, prevWavg: b, changePct: a != null && b != null && b !== 0 ? Math.round((a - b) / b * 10000) / 100 : null });
    return [
      { title: 'BASE', z: zoneOf(baseOf(hours), baseOf(prev)) },
      ...defs.map(([title, pick]) => ({ title, z: zoneOf(hours ? vwap(hours, pick) : null, prev ? vwap(prev, pick) : null) })),
    ];
  }, [hours, prev, t]);

  const rows = useMemo(() => (hours ?? []).map((h) => ({ hh: String(h.hour).padStart(2, '0'), price: h.price, volume: h.volume })), [hours]);
  const D1 = i18n.t('ak_prices_uah_mwh_vs_d_1');
  const has = rows.some((r) => r.price != null);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" style={{ opacity: loading && !hours ? 0.5 : 1, transition: 'opacity .15s' }}>
        {zones.map((x) => <RdZoneTile key={x.title} title={x.title} z={has ? x.z : undefined} sub={D1} />)}
      </div>
      <div className="flex justify-end"><RdViewSwitch /></div>
      <div className="tile p-4 sm:p-5">
        <div className="font-display text-[15px] font-semibold mb-3" style={{ color: 'var(--text)' }}>{t('pv2_idm_hourly')}</div>
        {loading && !hours ? (
          <RdEmpty text={t('ak_prices_loading')} height={360} />
        ) : !has ? (
          <RdEmpty height={360} text={t('pv2_idm_empty', { d: dmy(date) })} />
        ) : view === 'chart' ? (
          <div style={{ height: 420 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rows} margin={{ top: 10, right: 4, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                <XAxis dataKey="hh" tick={{ fontSize: 10, fill: c.dim }} axisLine={false} tickLine={false} interval={0} />
                <YAxis yAxisId="price" domain={[0, 16000]} ticks={PRICE_TICKS} width={64} tick={{ fontSize: 10, fill: c.dim }} axisLine={false} tickLine={false} tickFormatter={(v: number) => v.toLocaleString(loc())} label={{ value: t('ak_prices_price_uah_mwh'), angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: c.muted, textAnchor: 'middle' } }} />
                <YAxis yAxisId="vol" orientation="right" width={56} tick={{ fontSize: 10, fill: c.dim }} axisLine={false} tickLine={false} label={{ value: t('ak_prices_volume_mwh'), angle: 90, position: 'insideRight', style: { fontSize: 11, fill: c.muted, textAnchor: 'middle' } }} />
                <Tooltip contentStyle={{ background: c.surface, border: `1px solid ${c.grid}`, borderRadius: 10, fontSize: 12, color: c.text }} formatter={(v: any, n: any) => (n === t('ak_prices_price') ? [t('ak_prices_uah_mwh', { n: num(v, 0) }), n] : [t('ak_prices_mwh', { n: num(v, 1) }), n])} labelFormatter={(l: string) => t('ak_prices_hour_00', { h: l })} />
                <Legend wrapperStyle={{ fontSize: 12, color: c.muted }} />
                <Bar yAxisId="vol" dataKey="volume" name={t('ak_prices_volume')} fill={c.vol} fillOpacity={0.18} stroke={c.vol} strokeWidth={1} maxBarSize={26} />
                <Line yAxisId="price" type="monotone" dataKey="price" name={t('ak_prices_price')} stroke={c.price} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <RdTable hours={rows.map((r) => r.hh)} rows={[
            { label: t('ak_prices_price_uah_mwh'), cells: rows.map((r) => num(r.price, 0)), color: c.price },
            { label: t('ak_prices_volume_mwh'), cells: rows.map((r) => num(r.volume, 1)), color: c.vol },
          ]} />
        )}
      </div>
    </div>
  );
}

// ── БР: чіпи-підсумки + погодинний графік/таблиця небалансів ──
function BrRedesign({ endpoint, date, view }: { endpoint: string; date: string; view: View }) {
  const { t } = useTranslation();
  const c = useChartColors();
  const { setRange, setDate } = useMarketPricesUi();
  const [data, setData] = useState<BrResp | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    setLoading(true);
    authFetch(`${API_BASE_URL}${endpoint}${date ? `?date=${date}` : ''}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: BrResp | null) => {
        if (dead) return;
        setData(d);
        if (d) { setRange(d.dataRange?.min ?? null, d.dataRange?.max ?? null); if (!date && d.tradeDate) setDate(d.tradeDate); }
      })
      .catch(() => { if (!dead) setData(null); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [endpoint, date]);

  const hours: BrHour[] = useMemo(() => (data?.hours || []).map((h) => ({ ...h, hh: String(h.hour).padStart(2, '0') })), [data]);
  const sm = data?.summary;
  const POS = c.ok, NEG = c.warn;
  const csv = hours.length ? () => downloadCsv(`br_${endpoint.includes('oper') ? 'oper' : 'fact'}_${data?.tradeDate || date}`,
    [t('ak_prices_hour'), t('ak_prices_positive_imbalance_uah_mwh_2'), t('ak_prices_negative_imbalance_uah_mwh_2')],
    hours.map((h) => [h.hh, h.pos, h.neg])) : undefined;

  const chip = (label: string, value: number | null | undefined, color: string) => (
    <span key={label} className="inline-flex items-center gap-1.5 rounded-full" style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', padding: '5px 12px', fontSize: 11, whiteSpace: 'nowrap' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span><b style={{ color }}>{num2(value)}</b>
    </span>
  );
  const BrTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const get = (k: string) => payload.find((p: any) => p.dataKey === k)?.value;
    return (
      <div style={{ background: c.surface, border: `1px solid ${c.grid}`, borderRadius: 10, padding: '8px 12px', fontSize: 12, color: c.text }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{dmy(data?.tradeDate || date)} · {t('ak_prices_hour_00', { h: label })}</div>
        <div style={{ color: POS }}>{t('ak_prices_positive_imbalance_uah_mwh', { v: num2(get('pos')) })}</div>
        <div style={{ color: NEG }}>{t('ak_prices_negative_imbalance_uah_mwh', { v: num2(get('neg')) })}</div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end"><RdViewSwitch onCsv={csv ?? (() => {})} /></div>
      <div className="tile p-4 sm:p-5">
        {sm && (
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {chip(t('ak_prices_min_positive'), sm.minPos, POS)}
            {chip(t('ak_prices_max_positive'), sm.maxPos, POS)}
            {chip(t('ak_prices_min_negative'), sm.minNeg, NEG)}
            {chip(t('ak_prices_max_negative'), sm.maxNeg, NEG)}
          </div>
        )}
        {loading && !data ? (
          <RdEmpty text={t('ak_prices_loading')} height={420} />
        ) : hours.length === 0 ? (
          <RdEmpty text={<>{t('ak_prices_no_imbalance_prices_for', { d: dmy(date) })}</>} height={420} />
        ) : view === 'chart' ? (
          <div style={{ height: 440 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={hours} margin={{ top: 10, right: 12, bottom: 4, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                <XAxis dataKey="hh" tick={{ fontSize: 10, fill: c.dim }} axisLine={false} tickLine={false} interval={0} />
                <YAxis width={72} tick={{ fontSize: 10, fill: c.dim }} axisLine={false} tickLine={false} tickFormatter={(v: number) => v.toLocaleString(loc())} label={{ value: t('ak_prices_balancing_market_price_uah_mwh'), angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: c.muted, textAnchor: 'middle' } }} />
                <Tooltip content={<BrTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, color: c.muted }} />
                <Line type="monotone" dataKey="pos" name={t('ak_prices_positive_imbalance')} stroke={POS} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls />
                <Line type="monotone" dataKey="neg" name={t('ak_prices_negative_imbalance')} stroke={NEG} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <RdTable hours={hours.map((h) => h.hh)} rows={[
            { label: t('ak_prices_positive_imbalance_uah_mwh_2'), cells: hours.map((h) => num2(h.pos)), color: POS },
            { label: t('ak_prices_negative_imbalance_uah_mwh_2'), cells: hours.map((h) => num2(h.neg)), color: NEG },
          ]} />
        )}
      </div>
    </div>
  );
}

function RedesignMarketPrices() {
  const { t } = useTranslation();
  const { tab, date, view, period, setPeriod, setBrKind } = useMarketPricesUi();
  const src = (txt: string, href: string, label: string) => (
    <>{txt} · <a href={href} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: 'var(--series-3)' }}>{label}</a></>
  );
  const TITLES: Record<Exclude<Tab, 'rdn'>, { h: string; sub: ReactNode }> = {
    idm: { h: t('pv2_idm_title'), sub: src(t('ak_prices_source_market_operator_jsc'), OREE_URL, 'www.oree.com.ua') },
    br_oper: { h: t('ak_prices_operational_balancing_market_prices'), sub: src(t('ak_prices_source_npc_ukrenergo'), UA_ENERGY_URL, 'ua.energy') },
    br_fact: { h: t('ak_prices_actual_balancing_market_prices'), sub: src(t('ak_prices_source_npc_ukrenergo'), UA_ENERGY_URL, 'ua.energy') },
  };
  // РДН: без заголовка й підзаголовка (нарада 28.10: «Ціни» вже в топбарі, дата — у селекторі). ВДР і БР — зі своїм
  // заголовком (він їх розрізняє). Перемикач «За добу / За місяць» — окремим рядком над плитками, не в рядку заголовка.
  const ttl = tab === 'rdn' ? null : TITLES[tab];
  const isBr = tab === 'br_oper' || tab === 'br_fact';
  return (
    <DashboardLayout breadcrumb={[t('ak_prices_prices')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className={`px-4 md:px-6 pt-5 pb-10 ${pageMaxClass(1440)}`} style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>
          {ttl && (
            <div className="mb-4">
              <h1 className="font-display text-2xl font-semibold leading-tight" style={{ color: 'var(--text)' }}>{ttl.h}</h1>
              <p className="text-[13px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{ttl.sub}</p>
            </div>
          )}
          <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              {tab === 'rdn' && (
                <SegmentedTabs items={[{ key: 'day', label: t('pv2_period_day') }, { key: 'month', label: t('pv2_period_month') }]}
                  activeKey={period} onSelect={(k) => setPeriod(k as MarketPeriod)} />
              )}
              {isBr && (
                <SegmentedTabs items={[{ key: 'br_oper', label: t('pv2_br_oper') }, { key: 'br_fact', label: t('pv2_br_fact') }]}
                  activeKey={tab} onSelect={(k) => setBrKind(k as Tab)} />
              )}
            </div>
            {!isBr && <RdDownloadBtn kind="cards" title={t('pv2_download_cards')} />}
          </div>
          {tab === 'rdn'
            ? (period === 'month'
              ? <RdnPricesMonth date={date} view={view} controls={<RdViewSwitch />} />
              : <RdnPricesDay date={date} view={view} controls={<RdViewSwitch />} />)
            : tab === 'idm' ? <IdmRedesign date={date} view={view} />
              : tab === 'br_fact' ? <BrRedesign key="br_fact" endpoint="/market/br-fact-hourly" date={date} view={view} />
                : <BrRedesign key="br_oper" endpoint="/market/br-oper-hourly" date={date} view={view} />}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default function MarketPricesPage() {
  const isRd = import.meta.env.VITE_REDESIGN === '1';
  return isRd ? <RedesignMarketPrices /> : <LegacyMarketPrices />;
}
